import axios from 'axios';
import { getOpenSeaApiKey } from '../../constants/connectors';
import { HIDELIST_CONTRACTS } from '../../constants/hidelist-contracts';
import { INFT } from '../../types/nft';

export type OSApiGetByWalletProps = (
  account: string,
  assetCallback: (assets: INFT[]) => void,
  chainId?: number,
) => Promise<void>;

export const getNFTsByWallet: OSApiGetByWalletProps = async (account, assetCallback, chainId): Promise<void> => {
  const headers = getOpenSeaApiKey(chainId);
  let i = 0;
  while (true) {
    const OSApiResponse = await axios.get<OSApiDto>(
      `https://${
        chainId !== 1 ? 'testnets-' : ''
      }api.opensea.io/api/v1/assets?owner=${account}&order_direction=desc&limit=50&offset=${i * 50}`,
      {
        headers,
      },
    );
    if (OSApiResponse.data.assets.length) {
      const mappedAPIData: INFT[] = await Promise.all(
        OSApiResponse.data.assets
          .filter(
            (nft) =>
              // filter NFTs created with OS collection manager, reason - https://github.com/niftyhorde/swap-kiwi-frontend/issues/202
              !HIDELIST_CONTRACTS.includes(nft.asset_contract.address) &&
              nft.asset_contract.name !== 'OpenSea Collection' &&
              nft.asset_contract.name !== 'OpenSea Collections',
          )
          .map(
            async (nft): Promise<INFT> => {
              return {
                //to match id format from subgraph
                id: `${nft.id}-${nft.asset_contract.address}`,
                contractAddress: nft.asset_contract.address,
                tokenId: nft.token_id,
                name: nft.name,
                issuer: nft.asset_contract.name,
                imgUrl: nft.image_url,
                description: nft.description,
                amount: '0',
                openseaUrl: nft.permalink,
                verified: nft?.collection['safelist_request_status'] === 'verified',
                slug: nft?.collection.slug,
                type: nft.asset_contract.schema_name,
              };
            },
          ),
      );
      assetCallback(mappedAPIData);
    } else {
      assetCallback([]);
      break;
    }
    i++;
  }
};

interface OSApiDto {
  assets: {
    animation_original_url: string | null;
    animation_url: string | null;
    asset_contract: {
      address: string;
      asset_contract_type: string;
      buyer_fee_basis_points: number;
      created_date: string;
      default_to_fiat: boolean;
      description: string | null;
      dev_buyer_fee_basis_points: number;
      dev_seller_fee_basis_points: number;
      external_link: string | null;
      image_url: string | null;
      name: string;
      nft_version: string | null;
      only_proxied_transfers: boolean;
      opensea_buyer_fee_basis_points: number;
      opensea_seller_fee_basis_points: number;
      opensea_version: string | null;
      owner: number;
      payout_address: string | null;
      schema_name: string;
      seller_fee_basis_points: number;
      symbol: string;
      total_supply: string | null;
    };
    background_color: string | null;
    collection: {
      banner_image_url: string | null;
      chat_url: string | null;
      created_date: string;
      default_to_fiat: boolean;
      description: string | null;
      dev_buyer_fee_basis_points: string;
      dev_seller_fee_basis_points: string;
      discord_url: string | null;
      display_data: {
        card_display_style: string;
        images: Array<unknown>;
      };
      external_url: string | null;
      featured: boolean;
      featured_image_url: string | null;
      hidden: boolean;
      image_url: string | null;
      instagram_username: string | null;
      is_subject_to_whitelist: boolean;
      large_image_url: string | null;
      medium_username: string | null;
      name: string;
      only_proxied_transfers: boolean;
      opensea_buyer_fee_basis_points: string;
      opensea_seller_fee_basis_points: string;
      payout_address: string | null;
      require_email: boolean;
      safelist_request_status: string;
      short_description: string | null;
      slug: string;
      telegram_url: string | null;
      twitter_username: string | null;
      wiki_url: string | null;
    };
    creator: {
      address: string;
      config: string;
      profile_img_url: string;
      user: string | null;
    };
    decimals: string | null;
    description: string;
    external_link: string;
    id: number;
    image_original_url: string;
    image_preview_url: string;
    image_thumbnail_url: string;
    image_url: string;
    is_presale: boolean;
    last_sale: string | null;
    listing_date: string | null;
    name: string;
    num_sales: number;
    owner: {
      address: string;
      config: string;
      profile_img_url: string;
      user: {
        username: string;
      };
    };
    permalink: string;
    sell_orders: string | null;
    token_id: string;
    token_metadata: string;
    top_bid: string | null;
    traits: Array<unknown>;
    transfer_fee: string | null;
    transfer_fee_payment_token: string | null;
  }[];
}
